import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Settings, Mail, Save, RefreshCw, Info, Link, UserPlus, Image as ImageIcon, Plus, X, ListChecks, Wrench, Pencil, Check, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsPage = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activityTypes, setActivityTypes] = useState([]);
  const [newActivityType, setNewActivityType] = useState('');
  const [savingActivities, setSavingActivities] = useState(false);
  const [equipmentList, setEquipmentList] = useState([]);
  const [newEquipment, setNewEquipment] = useState('');
  const [savingEquipment, setSavingEquipment] = useState(false);
  const [editingEquipIdx, setEditingEquipIdx] = useState(null);
  const [editingEquipValue, setEditingEquipValue] = useState('');
  const [syncingHr, setSyncingHr] = useState(false);
  const [hrSyncResult, setHrSyncResult] = useState(null);
  const [hrPreview, setHrPreview] = useState(null);
  const [showHrConfirm, setShowHrConfirm] = useState(false);
  const [settings, setSettings] = useState({
    sender_email: '',
    company_email: '',
    app_url: '',
    logo_url: '',
    email_subject_template: '',
    email_body_intro: '',
    email_body_footer: '',
    email_attachment_note: '',
    welcome_email_subject: '',
    welcome_email_intro: '',
    welcome_email_footer: ''
  });

  useEffect(() => {
    fetchSettings();
    fetchActivityTypes();
    fetchEquipment();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/email`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      if (error.response?.status === 401) {
        toast.error('Sessione scaduta. Effettua nuovamente il login.');
      } else if (error.response?.status === 403) {
        toast.error('Accesso negato. Solo gli amministratori possono accedere.');
      } else {
        toast.error('Errore nel caricamento delle impostazioni: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityTypes = async () => {
    try {
      const res = await axios.get(`${API}/settings/activity-types`);
      setActivityTypes(res.data.types || []);
    } catch { /* ignora - verranno usati i default */ }
  };

  const handleSaveActivityTypes = async (typesToSave) => {
    setSavingActivities(true);
    try {
      const res = await axios.put(`${API}/settings/activity-types`, { types: typesToSave });
      setActivityTypes(res.data.types);
      toast.success('Tipologie attività aggiornate!');
    } catch (error) { toast.error(error.response?.data?.detail || 'Errore'); }
    finally { setSavingActivities(false); }
  };

  const addActivityType = () => {
    const trimmed = newActivityType.trim();
    if (!trimmed) return;
    if (activityTypes.includes(trimmed)) { toast.error('Tipologia già presente'); return; }
    const updated = [...activityTypes, trimmed].sort();
    setActivityTypes(updated);
    setNewActivityType('');
    handleSaveActivityTypes(updated);
  };

  const removeActivityType = (type) => {
    const updated = activityTypes.filter(t => t !== type);
    setActivityTypes(updated);
    handleSaveActivityTypes(updated);
  };

  const fetchEquipment = async () => {
    try {
      const res = await axios.get(`${API}/equipment`);
      setEquipmentList(res.data.equipment || []);
    } catch { /* ignora */ }
  };

  const handleSaveEquipment = async (items) => {
    setSavingEquipment(true);
    try {
      const res = await axios.put(`${API}/equipment`, { equipment: items });
      setEquipmentList(res.data.equipment);
      toast.success('Attrezzature aggiornate!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio attrezzature');
      fetchEquipment();
    } finally { setSavingEquipment(false); }
  };

  const addEquipment = () => {
    const trimmed = newEquipment.trim();
    if (!trimmed) return;
    if (equipmentList.some(e => e.toLowerCase() === trimmed.toLowerCase())) { toast.error('Attrezzatura già presente'); return; }
    setNewEquipment('');
    handleSaveEquipment([...equipmentList, trimmed]);
  };

  const removeEquipment = (name) => {
    if (equipmentList.length <= 1) { toast.error('Deve esserci almeno una attrezzatura'); return; }
    handleSaveEquipment(equipmentList.filter(e => e !== name));
  };

  const startEditEquipment = (idx) => {
    setEditingEquipIdx(idx);
    setEditingEquipValue(equipmentList[idx]);
  };

  const saveEditEquipment = () => {
    const trimmed = editingEquipValue.trim();
    if (!trimmed) { toast.error('Il nome non può essere vuoto'); return; }
    if (equipmentList.some((e, i) => i !== editingEquipIdx && e.toLowerCase() === trimmed.toLowerCase())) { toast.error('Attrezzatura già presente'); return; }
    const updated = equipmentList.map((e, i) => (i === editingEquipIdx ? trimmed : e));
    setEditingEquipIdx(null);
    setEditingEquipValue('');
    handleSaveEquipment(updated);
  };

  const handleHrSync = async () => {
    setSyncingHr(true);
    setHrSyncResult(null);
    try {
      const res = await axios.get(`${API}/integrations/hr/sync-preview`);
      if (!res.data.success) {
        toast.error(res.data.error || 'Errore nel contattare il portale HR');
        return;
      }
      setHrPreview(res.data);
      setShowHrConfirm(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel contattare il portale HR');
    } finally {
      setSyncingHr(false);
    }
  };

  const handleHrSyncConfirm = async () => {
    setShowHrConfirm(false);
    setSyncingHr(true);
    setHrSyncResult(null);
    try {
      const res = await axios.post(`${API}/integrations/hr/sync-from-hr`);
      setHrSyncResult(res.data);
      if (res.data.success) {
        toast.success('Sincronizzazione da HR completata!');
      } else {
        toast.error('Sincronizzazione parziale: controlla il dettaglio');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella sincronizzazione da HR');
    } finally {
      setSyncingHr(false);
    }
  };

  const handleSave = async () => {
    // Verifica che i campi essenziali non siano vuoti prima di salvare
    if (!settings.sender_email || !settings.email_subject_template) {
      toast.error('I campi Email Mittente e Template Oggetto sono obbligatori!');
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/settings/email`, settings);
      toast.success('Impostazioni salvate con successo!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Errore nel salvataggio delle impostazioni');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({
      sender_email: 'straordinari@splendorsud.com',
      company_email: 'straordinari@splendorsud.com',
      app_url: 'https://rls.splendorsud.com',
      logo_url: 'https://customer-assets.emergentagent.com/job_5956ea83-268b-47c4-a704-9204051c5002/artifacts/w25p61k6_0splendor%20vettoriale%202020%20S%20filigrana.jpg',
      email_subject_template: 'Rapporto Lavoro Straordinario - {site_name} - {date}',
      email_body_intro: 'Si prega di prendere visione del rapporto di lavoro straordinario in allegato.',
      email_body_footer: 'Splendor Sud S.r.l. - Sanificazioni e Pulizie dal 1970',
      email_attachment_note: 'In allegato il rapporto completo in formato PDF con le firme del dipendente e del cliente.',
      welcome_email_subject: 'Benvenuto in Splendor Sud - Credenziali di Accesso',
      welcome_email_intro: 'Il tuo account per l\'applicazione Rapporti di Lavoro Straordinario è stato creato con successo. Di seguito trovi le tue credenziali di accesso:',
      welcome_email_footer: 'Se hai domande o problemi con l\'accesso, contatta l\'amministratore.'
    });
    toast.info('Impostazioni ripristinate ai valori predefiniti. Clicca "Salva" per confermare.');
  };

  // Test email function (temporary)
  const [testingEmail, setTestingEmail] = useState(false);
  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const response = await axios.post(`${API}/admin/test-email`);
      const data = response.data;
      if (data.test_email_result?.includes('inviata')) {
        toast.success(`Email di test inviata a ${data.test_email_sent_to}`);
      } else {
        toast.error(`Problema: ${data.test_email_result}`);
      }
      console.log('Diagnostica email:', data);
      alert(`Diagnostica Email:\n\nSendGrid configurato: ${data.sendgrid_key_configured}\nApp URL DB: ${data.db_settings?.app_url}\nApp URL usato: ${data.app_url_used}\nSender usato: ${data.sender_used}\nRisultato: ${data.test_email_result}`);
    } catch (error) {
      toast.error('Errore nel test email');
      console.error(error);
    } finally {
      setTestingEmail(false);
    }
  };

  // Diagnose report by number
  const [diagnosingReport, setDiagnosingReport] = useState(false);
  const handleDiagnoseReport = async () => {
    const reportNumber = window.prompt('Inserisci il numero del rapporto da diagnosticare (es. 61/2026):');
    if (!reportNumber) return;
    
    setDiagnosingReport(true);
    try {
      const response = await axios.get(`${API}/admin/diagnose-report-by-number`, {
        params: { report_number: reportNumber }
      });
      const d = response.data;
      console.log('Diagnostica rapporto:', d);
      
      let diagText = `📋 DIAGNOSTICA RAPPORTO ${d.report_number}\n\n`;
      diagText += `📅 Data: ${d.date || d.date_range || 'N/A'}\n`;
      diagText += `🏗️ Cantiere: ${d.site_name}\n`;
      diagText += `📧 Email rapporto: ${d.report_client_email || 'Non impostata'}\n`;
      diagText += `📧 Email cantiere: ${d.site_client_email || 'Non impostata'}\n\n`;
      
      diagText += `📊 STATO: ${d.status?.toUpperCase() || 'N/A'}\n`;
      diagText += `✍️ Firma dipendente: ${d.has_employee_signature ? '✅ Presente' : '❌ Mancante'} (${d.employee_signer_name || '-'})\n`;
      diagText += `✍️ Firma cliente: ${d.has_client_signature ? '✅ Presente' : '❌ Mancante'} (${d.client_signer_name || '-'})\n`;
      diagText += `📤 Email inviata: ${d.email_sent ? '✅ Sì' : '❌ No'}\n`;
      if (d.email_sent_at) diagText += `   Inviata il: ${d.email_sent_at}\n`;
      if (d.approved_at) diagText += `✅ Approvato il: ${d.approved_at}\n`;
      if (d.rejected_at) diagText += `❌ Rifiutato il: ${d.rejected_at}\n`;
      if (d.rejection_reason) diagText += `   Motivo: ${d.rejection_reason}\n`;
      diagText += `\n🔍 DIAGNOSI:\n${d.diagnosis?.join('\n') || 'Nessuna'}\n`;
      
      if (d.email_logs?.length > 0) {
        diagText += `\n📬 LOG EMAIL (ultimi ${d.email_logs.length}):\n`;
        d.email_logs.forEach((log, i) => {
          diagText += `${i+1}. ${log.email_type} - ${log.status} - ${log.created_at}\n`;
          diagText += `   Destinatari: ${log.recipients?.join(', ')}\n`;
          if (log.error_message) diagText += `   Errore: ${log.error_message}\n`;
        });
      }
      
      if (d.approval_tokens?.length > 0) {
        diagText += `\n🔑 TOKEN APPROVAZIONE (ultimi ${d.approval_tokens.length}):\n`;
        d.approval_tokens.forEach((tok, i) => {
          diagText += `${i+1}. Creato: ${tok.created_at} - Usato: ${tok.used ? 'Sì' : 'No'} - Scade: ${tok.expires_at}\n`;
        });
      }
      
      alert(diagText);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rapporto non trovato');
      console.error(error);
    } finally {
      setDiagnosingReport(false);
    }
  };

  // Force approve report
  const [forcingApproval, setForcingApproval] = useState(false);
  const handleForceApprove = async () => {
    const reportNumber = window.prompt('Inserisci il numero del rapporto da APPROVARE FORZATAMENTE (es. 61/2026):');
    if (!reportNumber) return;
    
    const sendEmail = window.confirm(`Vuoi anche inviare l'email con il PDF al cliente?\n\nClicca OK per inviare l'email\nClicca Annulla per approvare senza inviare email`);
    
    if (!window.confirm(`⚠️ CONFERMA APPROVAZIONE FORZATA\n\nRapporto: ${reportNumber}\nInvio email: ${sendEmail ? 'Sì' : 'No'}\n\nQuesta azione:\n• Imposterà lo stato a "APPROVATO"\n• ${sendEmail ? 'Invierà l\'email con il PDF al cliente' : 'NON invierà email'}\n\nContinuare?`)) {
      return;
    }
    
    setForcingApproval(true);
    try {
      const response = await axios.post(`${API}/admin/force-approve-report`, null, {
        params: { report_number: reportNumber, send_email: sendEmail }
      });
      const r = response.data;
      toast.success(`Rapporto ${reportNumber} approvato!`);
      alert(`✅ APPROVAZIONE FORZATA COMPLETATA\n\nRapporto: ${r.report_id}\nStato precedente: ${r.old_status}\nNuovo stato: ${r.new_status}\n\n📧 Email: ${r.email_result}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'approvazione');
      console.error(error);
    } finally {
      setForcingApproval(false);
    }
  };

  // Fix all settings (temporary)
  const [fixingSettings, setFixingSettings] = useState(false);
  const handleFixAllSettings = async () => {
    if (!window.confirm('Vuoi ripristinare TUTTE le impostazioni email ai valori corretti?\n\nApp URL: https://rls.splendorsud.com\nMittente: straordinari@splendorsud.com')) {
      return;
    }
    setFixingSettings(true);
    try {
      const response = await axios.post(`${API}/admin/fix-all-settings`);
      toast.success('Impostazioni corrette con successo!');
      fetchSettings(); // Ricarica le impostazioni
    } catch (error) {
      toast.error('Errore nella correzione');
      console.error(error);
    } finally {
      setFixingSettings(false);
    }
  };

  // Recalculate special hours
  const [recalculating, setRecalculating] = useState(false);
  const handleRecalculateHours = async () => {
    if (!window.confirm('Vuoi ricalcolare le ore per TUTTI i rapporti esistenti?\n\nQuesta operazione:\n• Applica la tabella di conversione minuti-centesimi\n• Ricalcola ore totali, speciali e individuali\n• Corregge eventuali discrepanze di arrotondamento')) {
      return;
    }
    setRecalculating(true);
    try {
      const response = await axios.post(`${API}/admin/recalculate-all-special-hours`);
      toast.success(`${response.data.message}`);
    } catch (error) {
      toast.error('Errore nel ricalcolo');
      console.error(error);
    } finally {
      setRecalculating(false);
    }
  };

  // Assign missing report numbers
  const [assigningNumbers, setAssigningNumbers] = useState(false);
  const handleAssignNumbers = async () => {
    if (!window.confirm('Vuoi assegnare i numeri progressivi ai rapporti che ne sono privi?\n\nQuesta operazione:\n• Trova tutti i rapporti senza numero (escluse le bozze)\n• Assegna numeri in ordine cronologico per data\n• NON modifica i rapporti già numerati')) {
      return;
    }
    setAssigningNumbers(true);
    try {
      const response = await axios.post(`${API}/admin/assign-missing-report-numbers`);
      toast.success(`${response.data.message}`);
      if (response.data.updated_count > 0) {
        console.log('Rapporti numerati:', response.data.details);
      }
    } catch (error) {
      toast.error('Errore nell\'assegnazione numeri');
      console.error(error);
    } finally {
      setAssigningNumbers(false);
    }
  };

  // Migrate site emails to new format
  const [migratingEmails, setMigratingEmails] = useState(false);
  const handleMigrateEmails = async () => {
    if (!window.confirm('Vuoi migrare i cantieri al nuovo formato email?\n\nQuesta operazione:\n• Trova cantieri con email singola (formato legacy)\n• Converte al nuovo formato email multiple\n• L\'email esistente diventa "principale"\n• NON modifica cantieri già migrati\n\nÈ sicuro eseguirla più volte.')) {
      return;
    }
    setMigratingEmails(true);
    try {
      const response = await axios.post(`${API}/admin/migrate-site-emails`);
      if (response.data.migrated_count > 0) {
        toast.success(`Migrati ${response.data.migrated_count} cantieri!`);
        console.log('Cantieri migrati:', response.data.migrated_sites);
      } else {
        toast.info('Nessun cantiere da migrare. Tutti già aggiornati!');
      }
      alert(`Migrazione Email Cantieri:\n\n✅ Cantieri migrati: ${response.data.migrated_count}\n📊 Totale cantieri: ${response.data.stats.total_sites}\n📧 Con email configurate: ${response.data.stats.sites_with_emails}`);
    } catch (error) {
      toast.error('Errore nella migrazione');
      console.error(error);
    } finally {
      setMigratingEmails(false);
    }
  };

  if (!isAdmin()) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-red-500">Accesso negato. Solo gli amministratori possono accedere a questa pagina.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B6B93]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
            <Settings className="w-8 h-8 text-[#1B6B93]" />
            Impostazioni
          </h1>
          <p className="text-slate-500 mt-1">Gestisci le impostazioni dell'applicazione</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button onClick={handleRecalculateHours} disabled={recalculating} variant="outline" className="border-blue-500 text-blue-600 hover:bg-blue-50">
            {recalculating ? 'Ricalcolo...' : '🔄 Ricalcola Ore'}
          </Button>
          <Button onClick={handleAssignNumbers} disabled={assigningNumbers} variant="outline" className="border-purple-500 text-purple-600 hover:bg-purple-50">
            {assigningNumbers ? 'Assegnazione...' : '🔢 Numera Rapporti'}
          </Button>
          <Button onClick={handleMigrateEmails} disabled={migratingEmails} variant="outline" className="border-teal-500 text-teal-600 hover:bg-teal-50">
            {migratingEmails ? 'Migrazione...' : '📧 Migra Email'}
          </Button>
          <Button onClick={handleDiagnoseReport} disabled={diagnosingReport} variant="outline" className="border-indigo-500 text-indigo-600 hover:bg-indigo-50">
            {diagnosingReport ? 'Diagnosi...' : '🔍 Diagnosi Rapporto'}
          </Button>
          <Button onClick={handleForceApprove} disabled={forcingApproval} variant="outline" className="border-emerald-500 text-emerald-600 hover:bg-emerald-50">
            {forcingApproval ? 'Approvazione...' : '✅ Forza Approvazione'}
          </Button>
          <Button onClick={handleFixAllSettings} disabled={fixingSettings} variant="outline" className="border-red-500 text-red-600 hover:bg-red-50">
            {fixingSettings ? 'Correzione...' : '🔧 Correggi Tutto'}
          </Button>
          <Button onClick={handleTestEmail} disabled={testingEmail} variant="outline" className="border-orange-500 text-orange-600 hover:bg-orange-50">
            <Mail className="w-4 h-4 mr-2" />
            {testingEmail ? 'Invio...' : 'Test Email'}
          </Button>
          <Button onClick={handleReset} variant="outline" className="btn-outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Ripristina Default
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving || loading || !settings.sender_email || !settings.email_subject_template} 
            className="btn-primary"
            title={!settings.sender_email || !settings.email_subject_template ? "Compila i campi obbligatori prima di salvare" : ""}
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Salvataggio...' : 'Salva Modifiche'}
          </Button>
        </div>
      </div>

      {/* Email Settings Card */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#1B6B93]">
            <Mail className="w-5 h-5" />
            Impostazioni Email
          </CardTitle>
          <CardDescription>
            Configura gli indirizzi email e il template delle email inviate con i rapporti
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Email Addresses */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="sender_email">Email Mittente</Label>
              <Input
                id="sender_email"
                type="email"
                value={settings.sender_email}
                onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
                placeholder="noreply@splendorsud.com"
                className="input-field"
              />
              <p className="text-xs text-slate-500">
                L'indirizzo email che apparirà come mittente delle email
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="company_email">Email Azienda (Copia)</Label>
              <Input
                id="company_email"
                type="email"
                value={settings.company_email}
                onChange={(e) => setSettings({ ...settings, company_email: e.target.value })}
                placeholder="straordinari@splendorsud.com"
                className="input-field"
              />
              <p className="text-xs text-slate-500">
                L'indirizzo email dove l'azienda riceve copia di ogni rapporto inviato
              </p>
            </div>
          </div>

          {/* App URL */}
          <div className="space-y-2">
            <Label htmlFor="app_url" className="flex items-center gap-2">
              <Link className="w-4 h-4" />
              URL App (per email di benvenuto)
            </Label>
            <Input
              id="app_url"
              type="url"
              value={settings.app_url}
              onChange={(e) => setSettings({ ...settings, app_url: e.target.value })}
              placeholder="https://splendorsud.emergent.sh"
              className="input-field"
            />
            <div className="flex items-start gap-2 p-3 bg-green-50 rounded-lg">
              <UserPlus className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-green-700">
                Quando crei un nuovo dipendente, riceverà automaticamente un'email con le credenziali di accesso e un link a questo URL per aprire l'app.
              </p>
            </div>
          </div>

          <hr className="border-slate-200" />

          {/* Email Subject Template */}
          <div className="space-y-2">
            <Label htmlFor="email_subject_template">Oggetto Email</Label>
            <Input
              id="email_subject_template"
              value={settings.email_subject_template}
              onChange={(e) => setSettings({ ...settings, email_subject_template: e.target.value })}
              placeholder="Rapporto Lavoro Straordinario - {site_name} - {date}"
              className="input-field"
            />
            <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
              <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-700">
                <strong>Variabili disponibili:</strong> {'{site_name}'} = Nome cantiere, {'{date}'} = Data, {'{activity_type}'} = Tipo attività, {'{total_hours}'} = Ore totali
              </p>
            </div>
          </div>

          <hr className="border-slate-200" />

          {/* Email Body */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email_body_intro">Testo Introduttivo</Label>
              <Textarea
                id="email_body_intro"
                value={settings.email_body_intro}
                onChange={(e) => setSettings({ ...settings, email_body_intro: e.target.value })}
                placeholder="Si prega di prendere visione del rapporto di lavoro straordinario in allegato."
                className="input-field min-h-[80px]"
              />
              <p className="text-xs text-slate-500">
                Testo che appare all'inizio dell'email, prima dei dettagli del rapporto
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email_attachment_note">Nota Allegato</Label>
              <Textarea
                id="email_attachment_note"
                value={settings.email_attachment_note}
                onChange={(e) => setSettings({ ...settings, email_attachment_note: e.target.value })}
                placeholder="In allegato il rapporto completo in formato PDF con le firme del dipendente e del cliente."
                className="input-field min-h-[80px]"
              />
              <p className="text-xs text-slate-500">
                Testo che appare dopo i dettagli del rapporto, prima del footer
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email_body_footer">Footer Email</Label>
              <Input
                id="email_body_footer"
                value={settings.email_body_footer}
                onChange={(e) => setSettings({ ...settings, email_body_footer: e.target.value })}
                placeholder="Splendor Sud S.r.l. - Sanificazioni e Pulizie dal 1970"
                className="input-field"
              />
              <p className="text-xs text-slate-500">
                Testo che appare in fondo all'email
              </p>
            </div>
          </div>

          {/* Preview Section */}
          <hr className="border-slate-200" />
          <div className="space-y-3">
            <Label className="text-base font-semibold">Anteprima Email Rapporto</Label>
            <div className="border rounded-lg p-4 bg-white">
              <div className="text-center mb-4">
                <img 
                  src={settings.logo_url || "https://customer-assets.emergentagent.com/job_5956ea83-268b-47c4-a704-9204051c5002/artifacts/w25p61k6_0splendor%20vettoriale%202020%20S%20filigrana.jpg"} 
                  alt="Logo" 
                  className="h-12 mx-auto"
                />
              </div>
              <div className="bg-[#1B6B93] text-white p-4 text-center rounded-t-lg">
                <h2 className="text-lg font-bold m-0">Rapporto Lavoro Straordinario</h2>
              </div>
              <div className="p-4 bg-slate-50 rounded-b-lg">
                <p className="mb-4 text-sm">{settings.email_body_intro || 'Testo introduttivo...'}</p>
                <table className="w-full text-sm mb-4">
                  <tbody>
                    <tr><td className="py-1 font-bold">Data:</td><td>2025-12-30</td></tr>
                    <tr><td className="py-1 font-bold">Cantiere:</td><td>Esempio Cantiere</td></tr>
                    <tr><td className="py-1 font-bold">Orario:</td><td>08:00 - 12:00</td></tr>
                    <tr><td className="py-1 font-bold">Ore Totali:</td><td><strong>4.00h</strong></td></tr>
                    <tr><td className="py-1 font-bold">Attività:</td><td>Pulizia straordinaria</td></tr>
                  </tbody>
                </table>
                <p className="text-sm">{settings.email_attachment_note || 'Nota allegato...'}</p>
              </div>
              <div className="text-center text-xs text-slate-500 mt-3 p-2">
                <p>{settings.email_body_footer || 'Footer email...'}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logo Settings Card */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#1B6B93]">
            <ImageIcon className="w-5 h-5" />
            Logo Aziendale
          </CardTitle>
          <CardDescription>
            Configura il logo che appare nelle email e nei PDF
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="logo_url">URL Logo</Label>
            <Input
              id="logo_url"
              type="url"
              value={settings.logo_url}
              onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
              placeholder="https://example.com/logo.png"
              className="input-field"
            />
            <p className="text-xs text-slate-500">
              Inserisci l'URL di un'immagine (formati supportati: PNG, JPG, GIF). Il logo apparirà nelle email e nei PDF.
            </p>
          </div>
          {settings.logo_url && (
            <div className="space-y-2">
              <Label>Anteprima Logo</Label>
              <div className="border rounded-lg p-4 bg-slate-50 flex items-center justify-center">
                <img 
                  src={settings.logo_url} 
                  alt="Logo Preview" 
                  className="max-h-24 max-w-full object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Welcome Email Settings Card */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#1B6B93]">
            <UserPlus className="w-5 h-5" />
            Email di Benvenuto
          </CardTitle>
          <CardDescription>
            Personalizza l'email che viene inviata ai nuovi dipendenti con le credenziali di accesso
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="welcome_email_subject">Oggetto Email</Label>
            <Input
              id="welcome_email_subject"
              value={settings.welcome_email_subject}
              onChange={(e) => setSettings({ ...settings, welcome_email_subject: e.target.value })}
              placeholder="Benvenuto in Splendor Sud - Credenziali di Accesso"
              className="input-field"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="welcome_email_intro">Testo Introduttivo</Label>
            <Textarea
              id="welcome_email_intro"
              value={settings.welcome_email_intro}
              onChange={(e) => setSettings({ ...settings, welcome_email_intro: e.target.value })}
              placeholder="Il tuo account è stato creato con successo..."
              className="input-field min-h-[80px]"
            />
            <p className="text-xs text-slate-500">
              Testo che appare dopo il saluto iniziale "Ciao [Nome],"
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="welcome_email_footer">Nota Finale</Label>
            <Textarea
              id="welcome_email_footer"
              value={settings.welcome_email_footer}
              onChange={(e) => setSettings({ ...settings, welcome_email_footer: e.target.value })}
              placeholder="Se hai domande o problemi con l'accesso, contatta l'amministratore."
              className="input-field min-h-[60px]"
            />
            <p className="text-xs text-slate-500">
              Testo che appare prima del footer aziendale
            </p>
          </div>

          {/* Welcome Email Preview */}
          <hr className="border-slate-200" />
          <div className="space-y-3">
            <Label className="text-base font-semibold">Anteprima Email di Benvenuto</Label>
            <div className="border rounded-lg p-4 bg-white">
              <div className="text-center mb-4">
                <img 
                  src={settings.logo_url || "https://customer-assets.emergentagent.com/job_5956ea83-268b-47c4-a704-9204051c5002/artifacts/w25p61k6_0splendor%20vettoriale%202020%20S%20filigrana.jpg"} 
                  alt="Logo" 
                  className="h-12 mx-auto"
                />
              </div>
              <div className="bg-[#1B6B93] text-white p-4 text-center rounded-t-lg">
                <h2 className="text-lg font-bold m-0">Benvenuto in Splendor Sud!</h2>
              </div>
              <div className="p-4 bg-slate-50 rounded-b-lg">
                <p className="text-base mb-4">Ciao <strong>Mario Rossi</strong>,</p>
                <p className="mb-4 text-sm">{settings.welcome_email_intro || 'Testo introduttivo...'}</p>
                
                <div className="border-2 border-[#1B6B93] rounded-lg p-4 my-4 bg-white">
                  <table className="w-full text-sm">
                    <tbody>
                      <tr>
                        <td className="py-2 font-bold text-[#1B6B93] w-24">Email:</td>
                        <td className="py-2 font-mono bg-slate-100 px-2 rounded">mario.rossi@example.com</td>
                      </tr>
                      <tr>
                        <td className="py-2 font-bold text-[#1B6B93]">Password:</td>
                        <td className="py-2 font-mono bg-slate-100 px-2 rounded">********</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <div className="text-center my-4">
                  <span className="inline-block bg-[#1B6B93] text-white px-6 py-3 rounded-lg font-bold">
                    Accedi all'App
                  </span>
                </div>
                
                <p className="text-sm text-slate-600 mt-4">
                  <strong>⚠️ Importante:</strong> Ti consigliamo di cambiare la password al primo accesso.
                </p>
                
                <p className="text-sm text-slate-600 mt-4">{settings.welcome_email_footer || 'Nota finale...'}</p>
              </div>
              <div className="text-center text-xs text-slate-500 mt-3 p-2">
                <p>{settings.email_body_footer || 'Footer email...'}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Card Tipologie Attività */}
      <Card className="border-0 shadow-md">
        <CardHeader className="bg-gradient-to-r from-[#1B6B93] to-[#4BA3C7] text-white rounded-t-xl">
          <CardTitle className="flex items-center gap-2 text-lg"><ListChecks className="w-5 h-5" /> Tipologie Attività</CardTitle>
          <CardDescription className="text-white/80">Gestisci le tipologie di attività disponibili nei rapporti singoli e multi-giorno</CardDescription>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          {/* Aggiungi nuova */}
          <div className="flex gap-2">
            <Input
              placeholder="Nuova tipologia..."
              value={newActivityType}
              onChange={(e) => setNewActivityType(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addActivityType()}
              className="flex-1"
              data-testid="new-activity-type-input"
            />
            <Button onClick={addActivityType} disabled={savingActivities || !newActivityType.trim()} className="bg-[#1B6B93] hover:bg-[#155a7a]">
              <Plus className="w-4 h-4 mr-1" /> Aggiungi
            </Button>
          </div>

          {/* Lista tipologie */}
          <div className="space-y-1">
            {activityTypes.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">Nessuna tipologia configurata. Verranno usate le tipologie predefinite.</p>
            ) : (
              activityTypes.map((type) => (
                <div key={type} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg border hover:bg-slate-100 group">
                  <span className="text-sm font-medium">{type}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0 text-red-400 hover:text-red-600 hover:bg-red-100 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => removeActivityType(type)}
                    title="Elimina tipologia"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))
            )}
          </div>
          <p className="text-xs text-slate-400">{activityTypes.length} tipologie configurate</p>
        </CardContent>
      </Card>

      {/* Card Attrezzature */}
      <Card className="border-0 shadow-md">
        <CardHeader className="bg-gradient-to-r from-[#1B6B93] to-[#4BA3C7] text-white rounded-t-xl">
          <CardTitle className="flex items-center gap-2 text-lg"><Wrench className="w-5 h-5" /> Attrezzature</CardTitle>
          <CardDescription className="text-white/80">Gestisci le attrezzature disponibili nei rapporti: aggiungi, rinomina o elimina</CardDescription>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          {/* Aggiungi nuova */}
          <div className="flex gap-2">
            <Input
              placeholder="Nuova attrezzatura..."
              value={newEquipment}
              onChange={(e) => setNewEquipment(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addEquipment()}
              className="flex-1"
              data-testid="new-equipment-input"
            />
            <Button onClick={addEquipment} disabled={savingEquipment || !newEquipment.trim()} className="bg-[#1B6B93] hover:bg-[#155a7a]" data-testid="add-equipment-btn">
              <Plus className="w-4 h-4 mr-1" /> Aggiungi
            </Button>
          </div>

          {/* Lista attrezzature */}
          <div className="space-y-1">
            {equipmentList.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">Nessuna attrezzatura configurata.</p>
            ) : (
              equipmentList.map((name, idx) => (
                <div key={name} className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-50 rounded-lg border hover:bg-slate-100 group" data-testid={`equipment-row-${idx}`}>
                  {editingEquipIdx === idx ? (
                    <>
                      <Input
                        value={editingEquipValue}
                        onChange={(e) => setEditingEquipValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveEditEquipment(); if (e.key === 'Escape') setEditingEquipIdx(null); }}
                        className="flex-1 h-8 text-sm"
                        autoFocus
                        data-testid="edit-equipment-input"
                      />
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-100" onClick={saveEditEquipment} disabled={savingEquipment} title="Salva" data-testid="save-equipment-edit-btn">
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600 hover:bg-slate-200" onClick={() => setEditingEquipIdx(null)} title="Annulla" data-testid="cancel-equipment-edit-btn">
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <span className="text-sm font-medium flex-1">{name}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 text-[#1B6B93] hover:text-[#155a7a] hover:bg-blue-100 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => startEditEquipment(idx)}
                        title="Rinomina attrezzatura"
                        data-testid={`edit-equipment-btn-${idx}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 text-red-400 hover:text-red-600 hover:bg-red-100 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => removeEquipment(name)}
                        title="Elimina attrezzatura"
                        data-testid={`delete-equipment-btn-${idx}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
          <p className="text-xs text-slate-400">{equipmentList.length} attrezzature configurate</p>
        </CardContent>
      </Card>

      {/* Card Portale HR */}
      <Card className="border-0 shadow-md">
        <CardHeader className="bg-gradient-to-r from-indigo-600 to-indigo-400 text-white rounded-t-xl">
          <CardTitle className="flex items-center gap-2 text-lg"><Link className="w-5 h-5" /> Portale HR</CardTitle>
          <CardDescription className="text-white/80">Sincronizza dipendenti e cantieri dal portale HR (hr.splendorsud.com)</CardDescription>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 text-sm text-slate-600">
              <p>Importa l'anagrafica completa: i nuovi dipendenti vengono aggiunti, quelli cessati disattivati.</p>
              <p className="text-xs text-slate-400 mt-1">I dipendenti importati sono selezionabili nei rapporti ma non hanno accesso all'app.</p>
            </div>
            <Button onClick={handleHrSync} disabled={syncingHr} className="bg-indigo-600 hover:bg-indigo-700 shrink-0" data-testid="hr-sync-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${syncingHr ? 'animate-spin' : ''}`} />
              {syncingHr ? 'Sincronizzazione...' : 'Sincronizza da HR'}
            </Button>
          </div>
          {hrSyncResult && (
            <div className={`rounded-lg p-4 text-sm ${hrSyncResult.success ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`} data-testid="hr-sync-result">
              {hrSyncResult.employees && (
                <p>👷 Dipendenti: <strong>{hrSyncResult.employees.created}</strong> nuovi, <strong>{hrSyncResult.employees.updated}</strong> aggiornati{hrSyncResult.employees.deactivated > 0 && <>, <strong>{hrSyncResult.employees.deactivated}</strong> disattivati (cessati)</>}</p>
              )}
              {hrSyncResult.worksites && (
                <p>🏗️ Cantieri: <strong>{hrSyncResult.worksites.created}</strong> nuovi, <strong>{hrSyncResult.worksites.updated}</strong> aggiornati</p>
              )}
              {hrSyncResult.employees_error && <p className="text-red-600">Dipendenti: {hrSyncResult.employees_error}</p>}
              {hrSyncResult.worksites_error && <p className="text-red-600">Cantieri: {hrSyncResult.worksites_error}</p>}
              {hrSyncResult.error && <p className="text-red-600">{hrSyncResult.error}</p>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog conferma sync HR */}
      <Dialog open={showHrConfirm} onOpenChange={setShowHrConfirm}>
        <DialogContent className="max-w-lg" data-testid="hr-sync-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Conferma sincronizzazione da HR</DialogTitle>
            <DialogDescription>Controlla l'anteprima prima di procedere.</DialogDescription>
          </DialogHeader>
          {hrPreview && (
            <div className="space-y-3 text-sm">
              <p data-testid="hr-preview-counts">
                👷 Dipendenti ricevuti dal portale HR: <strong>{hrPreview.total_received}</strong>
                {hrPreview.new_count > 0 && <> — di cui <strong>{hrPreview.new_count}</strong> nuovi</>}
              </p>
              {hrPreview.to_deactivate_count > 0 ? (
                <div>
                  <p className="font-semibold text-red-600 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    {hrPreview.to_deactivate_count} {hrPreview.to_deactivate_count === 1 ? 'dipendente verrà disattivato (cessato)' : 'dipendenti verranno disattivati (cessati)'}:
                  </p>
                  <div className="mt-2 max-h-48 overflow-y-auto border border-red-200 rounded-lg bg-red-50 p-3 space-y-1" data-testid="hr-deactivate-list">
                    {hrPreview.to_deactivate.map((d, i) => (
                      <p key={i} className="text-xs text-red-800">{d.name} <span className="text-red-400">(matr. {d.matricola})</span></p>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">I dipendenti disattivati non compaiono più nei form, ma lo storico dei rapporti resta intatto. Se ricompaiono in HR verranno riattivati automaticamente.</p>
                </div>
              ) : (
                <p className="text-emerald-700" data-testid="hr-no-deactivate">✅ Nessun dipendente verrà disattivato.</p>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowHrConfirm(false)} data-testid="hr-sync-cancel-btn">Annulla</Button>
            <Button onClick={handleHrSyncConfirm} className="bg-indigo-600 hover:bg-indigo-700" data-testid="hr-sync-confirm-btn">Conferma sincronizzazione</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
